import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, ArrowLeft, Mail, Lock, Eye, EyeOff, UserCog, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamJoinPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    decodeInvite();
  }, [inviteCode]);

  const decodeInvite = () => {
    try {
      const decoded = JSON.parse(atob(inviteCode));
      setInviteData(decoded);
      setLoading(false);
    } catch (err) {
      setError('Invalid or expired invite link');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // For coaches, use the coach register-from-invite endpoint
      if (inviteData.type === 'coach') {
        await axios.post(`${API}/clubs/coach/register-from-invite`, {
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          team_id: inviteData.teamId,
          club_id: inviteData.clubId,
          role: inviteData.role || 'assistant'
        });
        toast.success('Account created! Please login.');
        navigate('/unified-login');
      } 
      // For managers, use the manager register-from-invite endpoint
      else if (inviteData.type === 'manager') {
        await axios.post(`${API}/clubs/manager/register-from-invite`, {
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          team_id: inviteData.teamId,
          club_id: inviteData.clubId
        });
        toast.success('Account created! Please login.');
        navigate('/unified-login');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create account';
      toast.error(message);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
          <h1 className="font-heading text-2xl uppercase mb-2">Invalid Invite</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-primary text-black hover:bg-primary/90">
              Go to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isCoach = inviteData?.type === 'coach';
  const roleColor = isCoach ? 'primary' : 'secondary';
  const RoleIcon = isCoach ? UserCog : Shield;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          <span className="text-sm uppercase tracking-widest">Back to Home</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className={`w-16 h-16 bg-${roleColor}/20 rounded-full flex items-center justify-center mx-auto mb-4`}>
            <RoleIcon className={`text-${roleColor}`} size={32} />
          </div>
          <h1 className="font-heading text-2xl uppercase mb-2">
            Join as <span className={`text-${roleColor}`}>{isCoach ? 'Team Coach' : 'Team Manager'}</span>
          </h1>
          <p className="text-muted-foreground text-sm">
            You've been invited to join a team
          </p>
        </motion.div>

        {/* Invite Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/40 rounded-sm p-4 mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Users className="text-emerald-400" size={20} />
            <span className="text-sm text-muted-foreground">Invitation Details</span>
          </div>
          <div className="space-y-2 text-sm">
            {inviteData?.clubName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Club:</span>
                <span className="font-medium">{inviteData.clubName}</span>
              </div>
            )}
            {inviteData?.teamName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Team:</span>
                <span className="font-medium">{inviteData.teamName}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role:</span>
              <span className={`font-medium text-${roleColor}`}>
                {isCoach ? (inviteData?.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach') : 'Team Manager'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Registration Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-card border border-border/40 rounded-sm p-6 space-y-5"
          data-testid="team-join-form"
        >
          <div>
            <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
              Full Name *
            </label>
            <Input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({...form, full_name: e.target.value})}
              required
              className={`bg-transparent border-border focus:border-${roleColor}`}
              placeholder="Your full name"
              data-testid="join-name-input"
            />
          </div>

          <div>
            <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                required
                className={`bg-transparent border-border pl-10 focus:border-${roleColor}`}
                placeholder="your@email.com"
                data-testid="join-email-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
              Create Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                required
                minLength={6}
                className={`bg-transparent border-border pl-10 pr-10 focus:border-${roleColor}`}
                placeholder="Min 6 characters"
                data-testid="join-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Permissions Info */}
          <div className={`bg-${roleColor}/10 border border-${roleColor}/30 rounded-sm p-3`}>
            <p className={`text-xs text-${roleColor} font-medium mb-1`}>
              {isCoach ? 'Coach Permissions' : 'Manager Permissions'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isCoach 
                ? 'You will have access to manage athletes, sessions, drills, and training plans for this team.'
                : 'You will have access to manage sessions, schedules, and team logistics (no technical tools).'
              }
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className={`w-full bg-${roleColor} text-black hover:bg-${roleColor}/90 uppercase tracking-wider py-6 font-bold`}
            data-testid="join-submit-btn"
          >
            {submitting ? 'Creating Account...' : 'Join Team'}
          </Button>
        </motion.form>

        {/* Already have account */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>
            Already have an account?{' '}
            <Link to="/team/login" className="text-primary hover:underline">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamJoinPage;
